<template>
  <div class="min-h-screen flex flex-col">
    <!-- Loading Overlay -->
    <div
      v-if="isLoading"
      class="fixed inset-0 bg-white z-50 flex items-center justify-center transition-opacity duration-300"
      :class="{ 'opacity-0 pointer-events-none': !isLoading }"
    >
      <LoadingSpinner />
    </div>
    <header class="bg-white text-black p-8">
      <div class="container mx-auto flex justify-between items-center">
        <h1 class="text-3xl font-bold">
          <NuxtLink
            :to="currentUser && !isLoadingPage ? '/property-search' : '/'"
          >
            <img src="~/assets/images/Logo_Temp.png" alt="Livia" class="h-8" />
          </NuxtLink>
        </h1>

        <!-- Mobile menu button -->
        <button @click="isMenuOpen = !isMenuOpen" class="md:hidden">
          <svg
            class="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              v-if="!isMenuOpen"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 6h16M4 12h16M4 18h16"
            />
            <path
              v-else
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <!-- Desktop Navigation -->
        <nav class="hidden md:block">
          <ul class="flex space-x-4">
            <template v-if="currentUser && !isLoadingPage">
              <li>
                <NuxtLink to="/profile-builder" class="hover:underline bold"
                  >Profile</NuxtLink
                >
              </li>
              <!-- <li>
                <NuxtLink to="/planning" class="hover:underline">Dashboard</NuxtLink>
              </li> -->
              <li>
                <NuxtLink to="/city-selection" class="hover:underline"
                  >Cities</NuxtLink
                >
              </li>
              <!-- <li>
                <NuxtLink to="/neighborhood-search" class="hover:underline">Neighborhoods</NuxtLink>
              </li> -->
              <li>
                <NuxtLink to="/property-search" class="hover:underline"
                  >Properties</NuxtLink
                >
              </li>
              <li>
                <NuxtLink to="/concierge" class="hover:underline"
                  >Concierge</NuxtLink
                >
              </li>
              <li>
                <NuxtLink to="/bookings" class="hover:underline"
                  >Bookings</NuxtLink
                >
              </li>
              <li>
                <a @click="logout" class="hover:underline cursor-pointer"
                  >Log Out</a
                >
              </li>
            </template>
            <template v-else-if="!isLoadingPage">
              <li>
                <NuxtLink to="/" class="hover:underline">Home</NuxtLink>
              </li>
              <li>
                <NuxtLink to="/about" class="hover:underline">About</NuxtLink>
              </li>
              <li>
                <NuxtLink to="/services" class="hover:underline"
                  >Services</NuxtLink
                >
              </li>
              <li>
                <NuxtLink to="/contact" class="hover:underline"
                  >Contact</NuxtLink
                >
              </li>
              <li>
                <NuxtLink to="/onboarding" class="hover:underline"
                  >Sign Up</NuxtLink
                >
              </li>
            </template>
          </ul>
        </nav>
      </div>

      <!-- Mobile Navigation -->
      <div v-show="isMenuOpen" class="md:hidden">
        <nav class="mt-4">
          <ul class="flex flex-col space-y-2">
            <template v-if="currentUser && !isLoadingPage">
              <li>
                <NuxtLink
                  to="/profile-builder"
                  class="block hover:underline py-2"
                  >Profile</NuxtLink
                >
              </li>
              <li>
                <NuxtLink to="/planning" class="block hover:underline py-2"
                  >Dashboard</NuxtLink
                >
              </li>
              <li>
                <NuxtLink
                  to="/city-selection"
                  class="block hover:underline py-2"
                  >Cities</NuxtLink
                >
              </li>
              <li>
                <NuxtLink
                  to="/property-search"
                  class="block hover:underline py-2"
                  >Properties</NuxtLink
                >
              </li>
              <li>
                <NuxtLink to="/concierge" class="block hover:underline py-2"
                  >Concierge</NuxtLink
                >
              </li>
              <li>
                <a
                  @click="logout"
                  class="block hover:underline py-2 cursor-pointer"
                  >Log Out</a
                >
              </li>
            </template>
            <template v-else-if="!isLoadingPage">
              <li>
                <NuxtLink to="/" class="block hover:underline py-2"
                  >Home</NuxtLink
                >
              </li>
              <li>
                <NuxtLink to="/about" class="block hover:underline py-2"
                  >About</NuxtLink
                >
              </li>
              <li>
                <NuxtLink to="/services" class="block hover:underline py-2"
                  >Services</NuxtLink
                >
              </li>
              <li>
                <NuxtLink to="/contact" class="block hover:underline py-2"
                  >Contact</NuxtLink
                >
              </li>
              <li>
                <NuxtLink to="/onboarding" class="block hover:underline py-2"
                  >Sign Up</NuxtLink
                >
              </li>
            </template>
          </ul>
        </nav>
      </div>
    </header>
    <main class="flex-grow">
      <NuxtPage />
      <Toaster richColors />
    </main>
    <footer class="bg-[#ECEDFC] py-6 mt-auto">
      <div class="container mx-auto px-4 py-4">
        <div
          class="flex flex-col md:flex-row md:justify-between md:items-center"
        >
          <div class="flex flex-col mb-4 md:mb-0">
            <div class="flex items-center mb-3">
              <img
                src="~/assets/images/Logo_Temp.png"
                alt="Livia"
                class="h-6 w-auto"
              />
            </div>
            <p class="text-gray-600 mb-2 max-w-md text-sm">
              Transforming the way people find and move into their dream homes
              with AI-powered solutions.
            </p>
            <p class="text-gray-600 text-sm italic">
              Part of the Lab378 Family
            </p>
          </div>
        </div>
        <div class="border-t border-gray-200 mt-4 pt-4">
          <p class="text-center text-gray-600 text-sm">
            © 2025 Livia. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  </div>
</template>

<style>
@import "tailwindcss/tailwind.css";
@import url("https://fonts.googleapis.com/icon?family=Material+Icons");

/* Add smooth transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

<script setup>
import { signOut } from "firebase/auth";
import { useCurrentUser } from "vuefire";
import { useConciergeStore } from "@/stores/ConciergeStore";

const isMenuOpen = ref(false);
const authStore = useAuthStore();
const auth = useFirebaseAuth();
const route = useRoute();
const currentUser = useCurrentUser();
const isLoading = ref(true);

const { $amplitude } = useNuxtApp();
$amplitude.track("Page View", {
  url: route.path,
});
// Compute whether we're on the loading page
const isLoadingPage = computed(() => {
  return route.path === "/profile-loading";
});

// Check if we're on the login or onboa;ading page
const isLoginOrOnboardingPage = computed(() => {
  return route.path === "/login" || route.path === "/onboarding";
});

// Handle loading state
onMounted(() => {
  // Add a small delay to ensure smooth transition
  setTimeout(() => {
    isLoading.value = false;
  }, 500);
});

const logout = async () => {
  const neighborhoodStore = useNeightbourhoodStore();
  const convoStore = useConvoStore();
  const personaStore = usePersonaStore();
  const propStore = usePropStore();
  const conciergeStore = useConciergeStore();

  try {
    await signOut(auth);
    neighborhoodStore.resetStore();
    personaStore.resetStore();
    convoStore.resetStore();
    propStore.resetStore();
    conciergeStore.resetStore();
    console.log("logout successful");
    localStorage.clear();
    authStore.isAuthenticated = false;
    authStore.userdetails = {};
    await navigateTo("/login");
  } catch (error) {
    console.error("Error signing out:", error);
  }
};
</script>